import React, {
  useRef,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import * as Icon from 'react-feather'
import { Transition } from 'react-transition-group'

import './Tooltip.scss'
import { positionCommentPlace } from '../../shared/constants'
import CommentContainer from '../CommentContainer/CommentContainer'
import { TooltipContext } from '../../context/tooltipContext'

const duration = 0

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  visibility: 'visible',
  opacity: 0,
}

const transitionStyles = {
  entered: { opacity: 1, visibility: 'visible' },
  exited: { opacity: 0, visibility: 'hidden' },
}

const Tooltip = () => {
  const {
    selectedTopic,
    tooltipPosition,
    tooltipPlace,
    isShowTooltip,
    onCloseTooltip,
  } = useContext(TooltipContext)

  const [comments, setComments] = useState([])
  const [isResolve, setIsResolve] = useState(null)

  useEffect(() => {
    if (selectedTopic !== null) {
      setComments(selectedTopic.comments)
      setIsResolve(selectedTopic.isResolve)
    } else {
      setComments([])
      setIsResolve(false)
    }
  }, [selectedTopic])

  const wrapperRef = useRef(null)
  const tailHeight = 8

  const handleClose = useCallback(
    (e) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }

      if (typeof onCloseTooltip === 'function') {
        onCloseTooltip()
      }
    },
    [onCloseTooltip]
  )

  /**
   * Clicks on the surrounding chrome - toolbox, comment panel, version panel,
   * header - sit above the Konva stage and never reach it, so CanvasView has no
   * chance to dismiss the editor for them. Handle those here, and deliberately
   * do not stop the event: the control the reviewer aimed at still fires, so
   * dismissing never costs them a second click on a button. Canvas clicks are
   * excluded because CanvasView owns them - it is the only place that knows
   * whether the click landed on a marker, and so whether to swallow it.
   */
  useEffect(() => {
    if (!isShowTooltip) return

    const handleOutsideMouseDown = (e) => {
      if (e.button !== 0) return
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return
      // .canvas-view is CanvasView's stage, .canvas-wrapper is CanvasContainer's
      if (
        e.target.closest &&
        e.target.closest('.canvas-view, .canvas-wrapper')
      ) {
        return
      }

      handleClose()
    }

    document.addEventListener('mousedown', handleOutsideMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown)
    }
  }, [handleClose, isShowTooltip])

  let classPosition = 'tooltip-top'
  let xPos = tooltipPosition ? tooltipPosition.x : 0
  let yPos = tooltipPosition ? tooltipPosition.y - tailHeight : 0

  if (tooltipPlace === positionCommentPlace.BOTTOM) {
    classPosition = 'tooltip-bottom'
    yPos = tooltipPosition ? tooltipPosition.y + tailHeight : 0
  }

  const defaultStyleRenderer = {
    ...defaultStyle,
    top: `${yPos}px`,
    left: `${xPos}px`,
  }

  return (
    <Transition in={isShowTooltip} timeout={150} unmountOnExit mountOnEnter>
      {(state) => (
        <div
          ref={wrapperRef}
          className="tooltip-wrapper"
          style={{
            ...defaultStyleRenderer,
            ...transitionStyles[state],
          }}
        >
          <div className="position-relative">
            <div className={`tooltip-content-wrapper ${classPosition}`}>
              <button
                type="button"
                className="btn btn-close"
                onClick={handleClose}
              >
                <Icon.XCircle className="close-icon" />
              </button>
              <div className="d-flex flex-column">
                <CommentContainer
                  comments={comments}
                  isResolve={isResolve}
                  onTooltipClose={handleClose}
                  isTooltip
                />
              </div>
            </div>

            <div className={`tooltip-tail ${classPosition}`}></div>
          </div>
        </div>
      )}
    </Transition>
  )
}

export default Tooltip
